// @ts-nocheck
import environment from "../config/environment";

/**
 * POST-based SSE stream consumer for the AVS OS AI search endpoint
 * (the avs-os proxy), including its `clarify` event.
 *
 * @param {Object} body - { question, sourceTypes?, chatId?, segment?, clarifyStep?, clarifyReply?, intentFinder? }
 * @param {Object} callbacks
 * @param {Function} callbacks.onSources   - called once with array of source objects
 * @param {Function} callbacks.onToken     - called per token chunk (string)
 * @param {Function} callbacks.onDone      - called once with { chatId, messageCount }
 * @param {Function} callbacks.onError     - called on stream or network error
 * @param {Function} callbacks.onClarify   - called when a clarify event is received
 */
export async function streamSearchAvsOs(body, { onSources, onToken, onDone, onError, onClarify }) {
  const url = environment.BASE_URL + "copilot/avs-os/chat";

  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "Authorization": "Bearer " + environment.BEARER_TOKEN,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errMsg = "Search failed";
    try {
      const errBody = await res.json();
      if (errBody.status === 401) {
        window.location.href = "/login";
        return;
      }
      errMsg = errBody.message || errMsg;
    } catch { /* ignore parse failure */ }
    const err = new Error(errMsg);
    onError?.(err);
    throw err;
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("text/event-stream")) {
    let errBody;
    try { errBody = await res.json(); } catch { errBody = {}; }
    if (errBody?.status === 401) {
      window.location.href = "/login";
      return;
    }
    const err = new Error(errBody?.message || "Unexpected response format");
    onError?.(err);
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullText = "";
  let sources = [];
  let result = null;

  let currentEvent = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith("data: ") && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));

          switch (currentEvent) {
            case "sources":
              sources = Array.isArray(data) ? data : [];
              onSources?.(sources);
              break;
            case "token":
              fullText += data.text || "";
              onToken?.(data.text || "");
              break;
            case "done":
              result = {
                chatId: data.chatId,
                messageCount: data.messageCount,
                sources,
                fullText,
              };
              onDone?.(result);
              break;
            case "error": {
              const streamErr = new Error(data.message || "Stream error");
              onError?.(streamErr);
              throw streamErr;
            }
            case "clarify":
              onClarify?.(data);
              break;
            default:
              break;
          }
        } catch (parseErr) {
          if (parseErr.message && parseErr.message !== "Stream error") {
            console.warn("AVS OS SSE parse warning:", parseErr);
          } else {
            throw parseErr;
          }
        }
        currentEvent = "";
      }
    }
  }

  if (!result) {
    result = { chatId: null, messageCount: 0, sources, fullText };
  }
  return result;
}
